import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function AsbestosSurveysPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whatWeSurvey = [
    {
      icon: 'ri-building-2-line',
      title: 'Building Materials',
      description: 'Insulation, ceiling tiles, floor tiles, roofing, siding, and pipe wrapping.',
    },
    {
      icon: 'ri-tools-line',
      title: 'Pre-Renovation Surveys',
      description: 'Identify asbestos before demolition or renovation to ensure safe work practices.',
    },
    {
      icon: 'ri-file-chart-line',
      title: 'Operations & Maintenance',
      description: 'Ongoing O&M programs for facilities with known asbestos-containing materials.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Compliance Documentation',
      description: 'Reports that meet EPA NESHAP, OSHA, and state asbestos regulations.',
    },
  ];

  const whoNeeds = [
    {
      title: 'Commercial Property Owners',
      description: 'Required before renovation or demolition of buildings constructed before 1980.',
    },
    {
      title: 'Schools & Institutions',
      description: 'AHERA-compliant asbestos management plans and ongoing inspections.',
    },
    {
      title: 'Industrial Facilities',
      description: 'Surveys for manufacturing plants, warehouses, and utility buildings.',
    },
    {
      title: 'Housing Portfolios',
      description: 'Multi-site asbestos surveys for military housing, public housing, and large portfolios.',
    },
    {
      title: 'Developers & Contractors',
      description: 'Due diligence surveys for acquisitions and pre-construction planning.',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Site Walk-Through',
      description: 'We review building age, construction type, and planned work to identify suspect materials.',
    },
    {
      step: '02',
      title: 'Sample Collection',
      description: 'Certified inspectors collect bulk samples from suspect materials following EPA protocols.',
    },
    {
      step: '03',
      title: 'Lab Analysis',
      description: 'Samples analyzed by accredited labs using polarized light microscopy (PLM).',
    },
    {
      step: '04',
      title: 'Survey Report & Plan',
      description: 'Detailed report with material locations, condition assessment, and management recommendations.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Asbestos Surveys & Consulting
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Professional asbestos surveys for renovation and demolition projects. We identify asbestos-containing materials, assess condition, and develop management plans that keep your project on track and compliant.
            </p>
            <Link
              to="/contact#contact-form"
              className="inline-block bg-[#f59e0b] text-white px-10 py-4 rounded-lg text-base font-semibold hover:bg-[#d97706] transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Request a Survey
            </Link>
          </div>
        </div>
      </section>

      {/* What We Survey */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What We Survey</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our asbestos surveys cover all building materials and situations where asbestos may be present.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatWeSurvey.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <i className={`${item.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs This */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Who Needs Asbestos Surveys?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Asbestos surveys are required before renovation or demolition of most buildings constructed before 1980.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whoNeeds.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Our Survey Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From initial walk-through to final report, we make asbestos surveys efficient and thorough.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300 h-full">
                  <div className="text-5xl font-bold text-[#f59e0b]/20 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose LCA */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Why Choose LCA for Asbestos Surveys?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-award-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">AHERA & EPA Certified</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                All inspectors are certified and trained in current EPA and OSHA protocols.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-time-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Fast Turnaround</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We understand project schedules—surveys and reports delivered on time, every time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-building-4-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Portfolio Experience</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Standardized methods and data systems for multi-site programs and large portfolios.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#f59e0b] to-[#d97706] rounded-3xl p-12 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Planning a Renovation or Demolition?
            </h2>
            <p className="text-xl text-white/95 mb-8 leading-relaxed">
              Get an asbestos survey before you start work. We'll identify the materials, assess the risk, and help you plan the next steps.
            </p>
            <Link
              to="/contact#contact-form"
              className="inline-block bg-white text-[#f59e0b] px-10 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Schedule a Survey
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
